<template>
  <NodeViewWrapper
    class="embedded-board show-selection ignore-editor-line-height max-w-full border border-solid border-important-border rounded-lg my-2"
    :class="{ 'w-full': node.attrs[constants.fullWidth] }"
  >
    <HeaderCustom
      class="rounded-t-lg"
      :resource-id="node.attrs['board-id']"
      :current-layout="node.attrs.layout"
      :resource-type="ResourceType.Board"
      @updateAttribute="updateAttribute"
    />
    <div class="relative">
      <ItemNotFound
        v-if="showItemNotFound"
        class="border rounded-lg"
      />
      <BoardView
        v-bind="node.attrs"
        class="rounded-lg"
        :editor-id="editor.id"
        :board-id="node.attrs['board-id']"
        :cards-layout="currentLayout"
      />
    </div>
  </NodeViewWrapper>
</template>

<script lang="ts">
export default {
  name: 'BoardEmbed',
};
</script>

<script setup lang="ts">
import { NodeViewWrapper } from '@tiptap/vue-3';
import { computed, onMounted, provide, ref, nextTick } from 'vue';
import { useStore } from 'vuex';
import BoardView from '../../pages/BoardView.vue';
import HeaderCustom from '../../widgets/HeaderCustom.vue';
import { ResourceType } from '@/types/resources';
import { Node } from '@tiptap/pm/model';
import EventBus from '@/utilities/eventBus';
import ItemNotFound from '@/components/widgets/ItemNotFound';
import constants from '@/utilities/constants';

const props = defineProps<{
  node: Node;
  updateAttributes: CallableFunction;
  deleteNode: CallableFunction;
  getPos: CallableFunction;
  editor: any;
}>();

const store = useStore();
const flatBoards = computed(() => store.getters.getFlatBoards);

const currentBoard = computed(() => flatBoards.value[props.node.attrs['board-id']] || {});
const localBoardLayout = ref(currentBoard.value?.layout);
const localGroupBy = ref('status');
const localSortBy = ref('manual');
const hideEmptyGroups = ref(false);

const currentLayout = computed(() => {
  return localBoardLayout.value || currentBoard.value?.layout;
});

onMounted(() => {
  // Update layout if it's updated on the board itself
  if ((currentBoard as any).value.layout !== props.node.attrs.layout && props.editor.id) {
    nextTick(() => {
      props?.updateAttributes?.({ layout: (currentBoard as any).value.layout });
    });
  }
});

const updateAttribute = (attr: any) => {
  const [key] = Object.keys(attr);
  switch (key) {
    case 'layout':
      localBoardLayout.value = attr[key];
      return;
    case 'group-by':
      localGroupBy.value = attr[key];
      return;
    case 'sort-by':
      localSortBy.value = attr[key];
      return;
    case 'hide-empty-groups':
      hideEmptyGroups.value = attr[key];
      return;
    case 'full-width':
      // used to force small screen view for table of content
      EventBus.$emit('updateFullWidth', {
        editorId: props.editor.id,
        value: attr[key],
        boardId: props.node.attrs['board-id'],
      });
  }
  props.updateAttributes(attr);
};

provide(
  'embeddedBoardAttributes',
  computed(() => ({
    boardId: props.node.attrs['board-id'],
    layout: currentLayout.value,
    fullWidth: props.node.attrs['full-width'],
    readOnly: props.node.attrs['read-only'],
    groupBy: localGroupBy.value,
    sortBy: localSortBy.value,
    getPos: props.getPos,
    editor: props.editor,
    hideEmptyGroups: hideEmptyGroups.value,
    deleteNode: props.deleteNode,
  }))
);
provide('embed', true);

const showItemNotFound = computed(() => {
  const boardId = props.node.attrs['board-id'];
  return flatBoards.value[`${boardId}_notFound`];
});
</script>

<style lang="scss">
.embedded-board {
  &::before {
    display: none;
  }
}
</style>
